/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {ORGANIZATION_ISSUE_TYPES_LIMIT} from '@github-ui/issue-types/constants.ts'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import type {ExtendedItemProps, SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {IssueTypePickerBase} from '@github-ui/item-picker-issue-type'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useAnalytics} from '@github-ui/use-analytics'
import {IssueOpenedIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {forwardRef, type RefObject, useCallback, useEffect, useMemo, useState} from 'react'
import {type Environment, fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'

import type {updateIssueIssueTypeBulkSharedMutation$data} from '../mutations/__generated__/updateIssueIssueTypeBulkSharedMutation.graphql'
import {commitUpdateIssueIssueTypeBulkSharedMutation} from '../mutations/update-issue-issue-type-bulk-shared-mutation'
import type {
  IssueTypePickerIssueTypeShared$data,
  IssueTypePickerIssueTypeShared$key,
} from './__generated__/IssueTypePickerIssueTypeShared.graphql'
import type {IssueTypePickerPaginatedShared$key} from './__generated__/IssueTypePickerPaginatedShared.graphql'
import type {IssueTypePickerSharedQuery} from './__generated__/IssueTypePickerSharedQuery.graphql'
import {IssueTypeDot} from './IssueTypeDot'
import styles from './IssueTypePicker.module.css'

export const UNSET_ID = 'UNSET'

const IssueTypeFragment = graphql`
  fragment IssueTypePickerIssueTypeShared on IssueType @inline {
    id
    name
    isEnabled
    description
    color
    pinnedFields {
      __typename
      ... on IssueFieldText {
        id
        name
        dataType
      }
      ... on IssueFieldSingleSelect {
        id
        name
        dataType
      }
      ... on IssueFieldDate {
        id
        name
        dataType
      }
      ... on IssueFieldNumber {
        id
        name
        dataType
      }
    }
  }
`
const IssueTypePickerPaginatedFragment = graphql`
  fragment IssueTypePickerPaginatedShared on Repository
  @argumentDefinitions(issueTypesPageSize: {type: "Int!"})
  @inline {
    issueTypes(first: $issueTypesPageSize) @connection(key: "Repository_issueTypes") {
      edges {
        node {
          ...IssueTypePickerIssueTypeShared
        }
      }
      totalCount
    }
  }
`

export const IssueTypePickerGraphqlSharedQuery = graphql`
  query IssueTypePickerSharedQuery($owner: String!, $repo: String!, $issueTypesPageSize: Int!) {
    repository(owner: $owner, name: $repo) {
      ...IssueTypePickerPaginatedShared @arguments(issueTypesPageSize: $issueTypesPageSize)
    }
  }
`

export async function fetchIssueTypes(
  environment: Environment,
  options: {
    owner: string
    repo: string
    pageSize: number
  },
): Promise<IssueTypePickerIssueType[]> {
  const {pageSize = 10, owner, repo} = options

  const data = await fetchQuery<IssueTypePickerSharedQuery>(
    environment,
    IssueTypePickerGraphqlSharedQuery,
    {
      owner,
      repo,
      issueTypesPageSize: pageSize,
    },
    {fetchPolicy: 'store-or-network'},
  ).toPromise()

  if (!data?.repository) {
    return []
  }

  // eslint-disable-next-line no-restricted-syntax
  const issueTypesData = readInlineData<IssueTypePickerPaginatedShared$key>(
    IssueTypePickerPaginatedFragment,
    data.repository,
  )

  return (issueTypesData?.issueTypes?.edges?.flatMap(a => a?.node) ?? [])
    .filter(node => !!node)
    .map(node => {
      // eslint-disable-next-line no-restricted-syntax
      return readInlineData<IssueTypePickerIssueTypeShared$key>(IssueTypeFragment, node)
    })
}

export type IssueTypePickerTestSharedQueries = {
  IssueTypePickerQuery: IssueTypePickerSharedQuery
}

export type IssueTypePickerIssueType = Omit<IssueTypePickerIssueTypeShared$data, ' $fragmentType'>

export type IssueTypePickerProps = {
  title?: string
  width?: 'small' | 'medium' | 'large'
  issueId?: string
  owner: string
  repo: string
  activeIssueType: IssueTypePickerIssueType | null
  preselectedIssueTypeName?: string
  showUnset?: boolean
  noTypeItem?: ExtendedItemProps<IssueTypePickerIssueType>
  readonly: boolean
  insidePortal?: boolean
  shortcutEnabled: boolean
  issueTypeToken?: string
  anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement>) => React.ReactElement
  onSelectionChange: (issueType: IssueTypePickerIssueType[]) => void
  onIssueUpdate?: () => void
  /**
   * Whether to render the issue type picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  ariaLabelledBy?: string
}

export type IssueTypePickerIssueTypeSharedKey = IssueTypePickerIssueTypeShared$key

export function readIssueTypeFromFragment(key: IssueTypePickerIssueTypeShared$key): IssueTypePickerIssueType {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData<IssueTypePickerIssueTypeShared$key>(IssueTypeFragment, key)
}

export function IssueTypePicker({shortcutEnabled, anchorElement, ...props}: IssueTypePickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-issue-type"
      anchorElement={anchorProps => anchorElement(anchorProps)}
      createChild={() => (
        <IssueTypePickerFetcher
          anchorElement={anchorProps => anchorElement(anchorProps)}
          shortcutEnabled={shortcutEnabled}
          {...props}
        />
      )}
    />
  )
}

function IssueTypePickerFetcher({owner, repo, anchorElement, ...props}: IssueTypePickerProps) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssueTypePickerPaginatedShared$key | null>(null)

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<IssueTypePickerSharedQuery>({
        environment,
        query: IssueTypePickerGraphqlSharedQuery,
        variables: {owner, repo, issueTypesPageSize: ORGANIZATION_ISSUE_TYPES_LIMIT},
      }).subscribe({
        next: internalData => {
          setData(internalData.repository ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })
    }
  }, [environment, fetchKey, owner, repo])

  // eslint-disable-next-line no-restricted-syntax
  const fetchedIssueTypesData = readInlineData<IssueTypePickerPaginatedShared$key>(
    IssueTypePickerPaginatedFragment,
    data,
  )

  const fetchedIssueTypes = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () =>
      fetchedIssueTypesData?.issueTypes?.edges?.flatMap(a =>
        // eslint-disable-next-line no-restricted-syntax
        a?.node ? [readInlineData<IssueTypePickerIssueTypeShared$key>(IssueTypeFragment, a.node)] : [],
      ) || [],
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    [fetchedIssueTypesData],
  )

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('types'),
    anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <IssueTypePickerBase
      repo={repo}
      owner={owner}
      isLoading={isLoading}
      anchorElement={anchorElement}
      fetchedIssueTypes={fetchedIssueTypes}
      {...props}
    />
  )
}

type BulkIssueIssueTypePickerProps = Omit<IssueTypePickerProps, 'issueId' | 'onSelectionChange'> &
  SharedBulkActionsItemPickerProps

export function BulkIssueIssueTypePicker({
  onError,
  onCompleted,
  issuesToActOn,
  activeIssueType,
  ...rest
}: BulkIssueIssueTypePickerProps) {
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const onSelectionChange = useCallback(
    (selectedIssueType: IssueTypePickerIssueType[]) => {
      const issueTypeId = selectedIssueType.length === 1 ? selectedIssueType[0]?.id : null
      if (!issueTypeId || issueTypeId === activeIssueType?.id) return
      const mutationArgs = {
        environment,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      sendAnalyticsEvent('issues_index.bulk_action_issue_type', 'BULK_ISSUE_ISSUE_TYPE_PICKER', {
        initialIssueTypeId: activeIssueType?.id ?? '',
        issueTypeId: issueTypeId === 'UNSET' ? '' : issueTypeId ?? '',
        issueIds: [...issuesToActOn],
      })

      commitUpdateIssueIssueTypeBulkSharedMutation({
        ...mutationArgs,
        input: {
          ids: [...issuesToActOn],
          issueTypeId,
        },
        onCompleted: ({updateIssuesBulk}: updateIssueIssueTypeBulkSharedMutation$data) => {
          onCompleted?.(updateIssuesBulk?.jobId || undefined)
        },
      })
    },
    [activeIssueType?.id, environment, issuesToActOn, onCompleted, onError, sendAnalyticsEvent],
  )
  return <IssueTypePicker {...rest} onSelectionChange={onSelectionChange} showUnset activeIssueType={activeIssueType} />
}

export function DefaultIssueTypeAnchor({
  activeIssueType,
  ariaLabelledBy,
  readonly,
  anchorProps,
  preselectedIssueTypeName,
}: Pick<IssueTypePickerProps, 'activeIssueType' | 'ariaLabelledBy' | 'readonly' | 'preselectedIssueTypeName'> & {
  anchorProps?: React.HTMLAttributes<HTMLElement> | undefined
}) {
  const selectedType = getDefaultIssueTypeAnchorLabel(activeIssueType, preselectedIssueTypeName)

  return (
    <Button
      {...anchorProps}
      trailingVisual={readonly ? null : TriangleDownIcon}
      aria-labelledby={ariaLabelledBy}
      disabled={readonly}
    >
      {/* calculated in a way so that the whole container is 160px wide */}
      <div className={clsx('css-truncate css-truncate-target', styles.issueTypePickerLabel)}>{selectedType}</div>
    </Button>
  )
}

const getDefaultIssueTypeAnchorLabel = (
  activeIssueType: IssueTypePickerIssueType | null,
  preselectedIssueTypeName?: string,
): string => {
  if (activeIssueType) return activeIssueType.name
  if (preselectedIssueTypeName) return preselectedIssueTypeName
  return 'None'
}

type DefaultIssueTypePickerFooterAnchorProps = Pick<IssueTypePickerProps, 'activeIssueType' | 'readonly'> & {
  anchorProps?: React.HTMLAttributes<HTMLElement> | undefined
}

export const DefaultIssueTypePickerFooterAnchor = forwardRef<
  HTMLButtonElement,
  DefaultIssueTypePickerFooterAnchorProps
>(({activeIssueType, readonly, anchorProps}, ref) => {
  return (
    <SharedPicker
      anchorText={LABELS.noIssueTypes}
      sharedPickerMainValue={activeIssueType?.name}
      anchorProps={readonly ? undefined : anchorProps}
      ariaLabel={LABELS.selectIssueTypes}
      readonly={readonly}
      leadingIcon={IssueOpenedIcon}
      hotKey={undefined}
      ref={ref}
      leadingIconElement={activeIssueType && <IssueTypeDot color={activeIssueType.color} />}
    />
  )
})

DefaultIssueTypePickerFooterAnchor.displayName = 'DefaultIssueTypePickerFooterAnchor'

try{ IssueTypePicker.displayName ||= 'IssueTypePicker' } catch {}
try{ IssueTypePickerFetcher.displayName ||= 'IssueTypePickerFetcher' } catch {}
try{ BulkIssueIssueTypePicker.displayName ||= 'BulkIssueIssueTypePicker' } catch {}
try{ DefaultIssueTypeAnchor.displayName ||= 'DefaultIssueTypeAnchor' } catch {}